import React from "react"
import { graphql, Link } from "gatsby"

import Layout from "../components/common/layout"
import SEO from "../components/common/seo"
import FaqSection from "../components/home/FaqSection"

const HIVE_SITE = "https://thehiveindex.com"

const AboutPage = ({ data }) => {
  const communityCount = data.communities.totalCount
  const topicCount = data.topics.totalCount
  const platformCount = data.platforms.totalCount

  const faqItems = [
    {
      q: "What is Hive Index?",
      a: `Hive Index is a free directory of online communities, organized by topic, platform, and features. It covers ${communityCount.toLocaleString()}+ communities across Discord, Slack, Reddit, Circle, Facebook Groups, and ${platformCount}+ other platforms, spanning ${topicCount}+ topic areas, from startup founders and software developers to writers, gamers, and beyond.`,
    },
    {
      q: "How do I find online communities on Hive Index?",
      a:
        "There are three ways to browse. You can search by keyword, browse by topic (e.g. Entrepreneurship, Design, or Crypto), or browse by platform (e.g. Discord servers, Slack groups, or Reddit communities). Each listing shows the platform, member count, features, and a direct link to join.",
    },
    {
      q: "What platforms does Hive Index cover?",
      a: `Hive Index covers communities across ${platformCount}+ platforms including Discord, Slack, Reddit, Circle, Facebook Groups, LinkedIn Groups, Telegram, and many more. We also list independent communities that run on their own custom software.`,
    },
    {
      q: "Is Hive Index free to use?",
      a:
        "Yes. Hive Index is completely free to browse and free to list your community. You can access all listings, filter by topic or platform, and see community details at no cost. Optional paid advertising and sponsored placements are available for builders who want extra visibility.",
    },
    {
      q: "Can I add my community to Hive Index?",
      a:
        "Absolutely. Submitting your community takes less than five minutes and is free. Once listed, your community gets a dedicated, SEO-indexed page that makes it discoverable to people actively searching for communities like yours. If it is already listed, you can claim and update the listing.",
    },
    {
      q: "How often is the directory updated?",
      a:
        "The directory is updated regularly. New communities are added continuously and existing listings are reviewed to keep member counts and information current.",
    },
    {
      q: "Who built Hive Index?",
      a:
        "Hive Index was built and is maintained by an independent founder. It started as a side project to solve the problem of community discovery and grew into the most comprehensive community directory on the internet.",
    },
    {
      q: "How do I suggest a new topic category?",
      a:
        "If you cannot find a topic that represents your community or interest area, use the Suggest a Topic page. We review all suggestions and curate new topic pages when there are enough communities to make it worthwhile.",
    },
  ]

  const pageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${HIVE_SITE}/about/#page`,
        url: `${HIVE_SITE}/about/`,
        name: "About Hive Index — The #1 Directory of Online Communities",
        isPartOf: { "@type": "WebSite", url: `${HIVE_SITE}/` },
      },
    ],
  }

  return (
    <Layout pageTitle="About The Hive Index" noBackground={true}>
      <SEO
        title="About Hive Index | The #1 Directory of Online Communities"
        description={`Hive Index is a free directory of ${communityCount.toLocaleString()}+ online communities across ${platformCount}+ platforms, organized by topic, platform, and features. Learn more about the project.`}
        path="/about/"
        schema={pageSchema}
      />

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Intro */}
        <div className="px-6 py-10 sm:px-10 sm:py-12">
          <div className="max-w-5xl mx-auto">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold text-rose-600 uppercase tracking-widest">
                About the project
              </p>
              <h1 className="mt-3 text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
                The internet's go-to community directory
              </h1>
              <p className="mt-5 text-lg text-gray-600 leading-relaxed">
                Online communities are scattered across dozens of platforms.
                Discord servers, Slack groups, Reddit communities, Circle
                spaces, Facebook groups, independent forums, thousands of places
                to find your people, and no consistent way to discover them.
              </p>
              <p className="mt-4 text-lg text-gray-600 leading-relaxed">
                Hive Index was built to fix that. Every listing is structured
                consistently: platform, member count, community features, and a
                direct link to join. No fluff, no dead links, no ghost towns
                disguised as active communities. Just the real ones.
              </p>
              <p className="mt-4 text-lg text-gray-600 leading-relaxed">
                It currently covers{" "}
                <Link
                  to="/communities/"
                  className="text-rose-600 font-semibold hover:underline"
                >
                  {communityCount.toLocaleString()} communities
                </Link>{" "}
                across{" "}
                <Link
                  to="/platforms/"
                  className="text-rose-600 font-semibold hover:underline"
                >
                  {platformCount} platforms
                </Link>
                , organized into{" "}
                <Link
                  to="/topics/"
                  className="text-rose-600 font-semibold hover:underline"
                >
                  {topicCount} topic categories
                </Link>
                . It is free to browse and free to list your community.
              </p>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        {/* <div className="border-t border-gray-100 bg-gray-50 px-6 py-8 sm:px-10">
          <div className="max-w-5xl mx-auto">
            <dl className="grid grid-cols-3 gap-6 max-w-lg">
              {[
                { label: "Communities", value: `${communityCount.toLocaleString()}` },
                { label: "Topic categories", value: `${topicCount}` },
                { label: "Platforms", value: `${platformCount}` },
              ].map((stat) => (
                <div key={stat.label}>
                  <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {stat.label}
                  </dt>
                  <dd className="mt-1 text-3xl font-extrabold text-gray-900">
                    {stat.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div> */}

        {/* How we think about quality */}
        <div className="border-t border-gray-100 px-6 py-10 sm:px-10">
          <div className="max-w-5xl mx-auto">
            <div className="max-w-3xl">
              <h2 className="text-xl font-extrabold text-gray-900">
                How listings work
              </h2>
              <p className="mt-4 text-base text-gray-600 leading-relaxed">
                Every community in the directory has a dedicated page with
                structured data: the platform it lives on, its member count, a
                description, and a set of feature tags (jobs board, events,
                mentorship, paid membership, etc.). This lets you filter and
                compare communities in a way that is impossible when they are
                scattered across the web.
              </p>
              <p className="mt-4 text-base text-gray-600 leading-relaxed">
                Community builders can{" "}
                <Link
                  to="/submit/"
                  className="text-rose-600 font-semibold hover:underline"
                >
                  submit a listing for free
                </Link>
                . Verified owners can also claim their listing to update
                details, add a logo, and see basic analytics on who is viewing
                and clicking through to their community.
              </p>
              <p className="mt-4 text-base text-gray-600 leading-relaxed">
                The Hive Index is maintained by a dedicated team. If you find a
                listing that is outdated, incorrect, or missing entirely,{" "}
                <Link
                  to="/suggest/"
                  className="text-rose-600 font-semibold hover:underline"
                >
                  make a suggestion
                </Link>
                .
              </p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="border-t border-gray-100 px-6 pb-10 sm:px-10">
          <div className="max-w-5xl mx-auto">
            <FaqSection faqs={faqItems} embedded />
          </div>
        </div>

        {/* CTA */}
        <div className="border-t border-gray-100 bg-yellow-50 px-6 py-10 sm:px-10">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-base font-semibold text-gray-900">
              Ready to explore?
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/topics/"
                className="inline-block rounded-md bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 transition-colors"
              >
                Browse by topic
              </Link>
              <Link
                to="/submit/"
                className="inline-block rounded-md border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                List your community
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export const query = graphql`
  query {
    communities: allGoogleSpreadsheetSourceCommunities(
      filter: { removed: { ne: true } }
    ) {
      totalCount
    }
    topics: allGoogleSpreadsheetSourceTopics(filter: { count: { gt: 0 } }) {
      totalCount
    }
    platforms: allGoogleSpreadsheetSourcePlatforms {
      totalCount
    }
  }
`

export default AboutPage
